import type {
  ProductCreateRequest,
  ProductListParams,
  ProductListResponse,
  ProductPriceCreateRequest,
  ProductPriceUpdateRequest,
  ProductResponse,
  ProductUpdateRequest,
} from '../types';
import type { RequestExtras, RequestFn } from '../client';

/**
 * A shop's product catalogue: what it sells, and at which prices.
 *
 * **Every route here belongs to one shop**, and the backend resolves it from
 * an `X-Profile-Id` header, answering `IR_04` without one. That is why
 * `profileId` is a required argument on every method rather than something to
 * remember in `options.headers`: a header that can be forgotten is forgotten,
 * and the failure arrives at runtime in a caller's dashboard rather than at
 * their compiler.
 *
 * Two shapes of the catalogue are worth knowing before calling this:
 *
 * * **A product is created with its prices.** The API requires at least one,
 *   because a product nobody can be charged for cannot be sold.
 * * **A price is closed, never rewritten.** `updatePrice` changes its label,
 *   its default flag and its metadata; the amount, the currency and the
 *   billing interval are fixed for the life of the price, so a charge that was
 *   made can always be explained by a price that still exists. Charging a new
 *   figure means adding a price and archiving the old one.
 * * **Every price route answers with the whole product.** The catalogue's unit
 *   is the product: a price means something only beside its siblings, and
 *   which one is the default changes the moment another is added. So these
 *   methods return `ProductResponse`, and the price that was touched is found
 *   in its `prices` — typing them as the price alone let a caller read
 *   `result.amount` as a number that is `undefined` at runtime, and mistake
 *   the product's id for a price id.
 */
export class Products {
  constructor(private readonly request: RequestFn) {}

  /** The shop scope every route here takes, as the backend expects it. */
  private scoped(profileId: string, options?: RequestExtras): RequestExtras {
    return { ...options, headers: { 'X-Profile-Id': profileId, ...options?.headers } };
  }

  /**
   * Create a product together with its prices.
   * `POST /products`
   *
   * @param params - The product, with at least one price.
   * @param profileId - The shop the product belongs to.
   */
  async create(
    params: ProductCreateRequest,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request('POST', '/products', {
      body: params,
      ...this.scoped(profileId, options),
    });
  }

  /**
   * One page of the shop's catalogue.
   * `GET /products/list`
   *
   * Archived products are returned too unless `status` narrows them away —
   * they are what past charges refer to.
   */
  async list(
    params: ProductListParams | undefined,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductListResponse> {
    return this.request('GET', '/products/list', {
      query: params === undefined ? undefined : { ...params },
      ...this.scoped(profileId, options),
    });
  }

  /**
   * Retrieve one product with its prices.
   * `GET /products/{product_id}`
   */
  async retrieve(
    productId: string,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request('GET', `/products/${encodeURIComponent(productId)}`, {
      ...this.scoped(profileId, options),
    });
  }

  /**
   * Change a product's name, description or metadata.
   * `POST /products/{product_id}`
   *
   * Its kind and its prices are not editable here — see the class note.
   */
  async update(
    productId: string,
    params: ProductUpdateRequest,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request('POST', `/products/${encodeURIComponent(productId)}`, {
      body: params,
      ...this.scoped(profileId, options),
    });
  }

  /**
   * Take a product off sale.
   * `POST /products/{product_id}/archive`
   *
   * It stays readable and keeps its prices: a payment made against it has to
   * remain explicable after it is withdrawn.
   */
  async archive(
    productId: string,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request('POST', `/products/${encodeURIComponent(productId)}/archive`, {
      ...this.scoped(profileId, options),
    });
  }

  /**
   * Put an archived product back on sale.
   * `POST /products/{product_id}/restore`
   */
  async restore(
    productId: string,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request('POST', `/products/${encodeURIComponent(productId)}/restore`, {
      ...this.scoped(profileId, options),
    });
  }

  /**
   * Add a price to an existing product.
   * `POST /products/{product_id}/prices`
   *
   * This is how a price changes: the new figure is added and the old price is
   * archived, so both remain readable.
   */
  async addPrice(
    productId: string,
    params: ProductPriceCreateRequest,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request('POST', `/products/${encodeURIComponent(productId)}/prices`, {
      body: params,
      ...this.scoped(profileId, options),
    });
  }

  /**
   * Change a price's label, its default flag or its metadata.
   * `POST /products/{product_id}/prices/{price_id}`
   *
   * Not its amount, its currency or its interval: see the class note.
   */
  async updatePrice(
    productId: string,
    priceId: string,
    params: ProductPriceUpdateRequest,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request(
      'POST',
      `/products/${encodeURIComponent(productId)}/prices/${encodeURIComponent(priceId)}`,
      { body: params, ...this.scoped(profileId, options) },
    );
  }

  /**
   * Take one price off sale, leaving the product on it.
   * `POST /products/{product_id}/prices/{price_id}/archive`
   */
  async archivePrice(
    productId: string,
    priceId: string,
    profileId: string,
    options?: RequestExtras,
  ): Promise<ProductResponse> {
    return this.request(
      'POST',
      `/products/${encodeURIComponent(productId)}/prices/${encodeURIComponent(priceId)}/archive`,
      { ...this.scoped(profileId, options) },
    );
  }
}
